// src/components/PhilosophyApproach.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const Section = styled.section`
    padding: 120px 0;
    position: relative;
    overflow: hidden; /* Keep hidden for section */

    @media (max-width: 768px) {
        padding: 80px 0;
    }
`;

const PhilosophySection = styled(Section)`
    background: linear-gradient(135deg,
    #ffffff 0%,
    #fafafa 50%,
    #ffffff 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg,
        transparent 0%,
        rgba(153, 1, 71, 0.2) 50%,
        transparent 100%);
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.02;
    background-image:
            radial-gradient(circle at 20% 30%, #990147 1px, transparent 0),
            radial-gradient(circle at 80% 70%, #cc3366 1px, transparent 0);
    background-size: 80px 80px;
`;

const Container = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 3rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 0 1rem;
    }
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: start;
    position: relative;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 4rem;
    }

    @media (max-width: 768px) {
        gap: 3rem;
    }
`;

// Left Column - Stays inside container
const TextContent = styled.div`
    position: relative;
    padding-right: 2rem;

    @media (max-width: 968px) {
        padding-right: 0;
        text-align: center;
    }
`;

// Additional Content
const AdditionalContent = styled.div`
    position: relative;
    padding-right: 2rem;

    @media (max-width: 968px) {
        padding-right: 0;
        text-align: center;
    }
`;

const SectionHeader = styled.div`
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        margin-bottom: 2rem;
    }
`;

const SectionTitle = styled(motion.h2)`
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #990147 50%, #cc3366 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;
    position: relative;

    @media (max-width: 1200px) {
        font-size: 3rem;
    }

    @media (max-width: 768px) {
        font-size: 2.5rem;
        margin-bottom: 1rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const TitleUnderline = styled(motion.div)`
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #990147, #cc3366);
    border-radius: 2px;
    margin-bottom: 2rem;

    @media (max-width: 968px) {
        margin-left: auto;
        margin-right: auto;
    }

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;
        width: 80px;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.3rem;
    color: #4a5568;
    line-height: 1.7;
    font-weight: 400;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);

    @media (max-width: 768px) {
        font-size: 1.1rem;
        line-height: 1.6;
    }
`;

const MainDescription = styled.div`
    p {
        font-size: 1.1rem;
        color: #4a5568;
        line-height: 1.8;
        margin-bottom: 2rem;
        font-weight: 400;

        &:last-child {
            margin-bottom: 0;
        }

        @media (max-width: 768px) {
            font-size: 1rem;
            line-height: 1.7;
            margin-bottom: 1.5rem;
        }
    }
`;

const HighlightBox = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(153, 1, 71, 0.95) 0%,
    rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 2.5rem;
    border-radius: 20px;
    margin: 2.5rem 0;
    box-shadow: 0 20px 40px rgba(153, 1, 71, 0.3);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        );
        pointer-events: none;
    }

    p {
        font-size: 1.2rem;
        font-weight: 500;
        line-height: 1.6;
        margin: 0;
        font-style: italic;
        position: relative;
        z-index: 2;
        color: #FFFFFF;
        text-align: center;

        @media (max-width: 768px) {
            font-size: 1rem;
            line-height: 1.5;
        }
    }

    @media (max-width: 768px) {
        padding: 2rem;
        margin: 2rem 0;
        border-radius: 16px;
    }
`;

// Additional Points Grid for Left Section - Inside container
const AdditionalPointsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: 3rem;
    position: relative;

    @media (max-width: 968px) {
        margin-top: 0;
    }

    @media (max-width: 768px) {
        gap: 1.5rem;
        margin-top: 0;
    }
`;

const AdditionalPointCard = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    padding: 2.5rem;
    position: relative;
    box-shadow:
            0 20px 40px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.6) inset,
            0 4px 20px rgba(153, 1, 71, 0.1) inset;
    transition: all 0.4s ease;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(135deg, #990147, #cc3366);
        border-radius: 20px 20px 0 0;
    }

    &:hover {
        transform: translateY(-8px) scale(1.02);
        box-shadow:
                0 30px 60px rgba(0, 0, 0, 0.12),
                0 0 0 1px rgba(255, 255, 255, 0.8) inset,
                0 8px 30px rgba(153, 1, 71, 0.15) inset;
    }

    @media (max-width: 768px) {
        padding: 2rem;
        border-radius: 16px;
    }
`;

// Timeline Components (Right Side) - Can extend outside
const TimelineContainer = styled.div`
    position: relative;
    overflow: visible; /* Allow overflow for timeline */

    @media (max-width: 968px) {
        overflow: hidden;
        margin: 0 auto;
        max-width: 100%;
    }
`;

const Timeline = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    overflow: visible; /* Allow overflow */

    &::before {
        content: '';
        position: absolute;
        top: 40px;
        left: -80px; /* Position outside the container */
        width: 3px;
        height: calc(100% - 80px);
        background: linear-gradient(to bottom,
        #990147 0%,
        #cc3366 100%);
        border-radius: 3px;
        z-index: 1;

        @media (max-width: 968px) {
            left: 30px; /* Center the line on mobile */
        }

        @media (max-width: 768px) {
            left: 25px;
            top: 30px;
            height: calc(100% - 60px);
        }

        @media (max-width: 480px) {
            left: 20px;
        }
    }

    @media (max-width: 768px) {
        gap: 1.5rem;
    }
`;

const TimelineItem = styled(motion.div)`
    position: relative;
    overflow: visible; /* Allow overflow */

    @media (max-width: 968px) {
        padding-left: 80px; /* Space for centered icon and line on mobile */
    }

    @media (max-width: 768px) {
        padding-left: 60px;
    }
`;

const TimelineCard = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    padding: 2.5rem 2.5rem 2.5rem 4rem; /* Extra left padding for spacing */
    position: relative;
    box-shadow:
            0 20px 40px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.6) inset,
            0 4px 20px rgba(153, 1, 71, 0.1) inset;
    transition: all 0.4s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: visible; /* Allow overflow */
    margin-left: 80px; /* Space for external elements */

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(135deg, #990147, #cc3366);
        border-radius: 20px 20px 0 0;
    }

    &:hover {
        transform: translateY(-8px) scale(1.02);
        box-shadow:
                0 30px 60px rgba(0, 0, 0, 0.12),
                0 0 0 1px rgba(255, 255, 255, 0.8) inset,
                0 8px 30px rgba(153, 1, 71, 0.15) inset;
    }

    @media (max-width: 968px) {
        margin-left: 0;
        padding: 2rem;
    }

    @media (max-width: 768px) {
        padding: 1.5rem;
        border-radius: 16px;
    }
`;

const TimelineIcon = styled(motion.div)`
    position: absolute;
    left: -30px; /* Position well outside the container */
    top: 50%;
    transform: translateY(-50%);
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #990147, #cc3366);
    box-shadow: 0 15px 30px rgba(153, 1, 71, 0.3);
    z-index: 2;

    &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, #990147, #cc3366);
        border-radius: 50%;
        z-index: -1;
        opacity: 0.6;
        filter: blur(10px);
    }

    .emoji {
        font-size: 2rem;
        filter: brightness(0) invert(1);
    }

    @media (max-width: 968px) {
        left: 0px; /* Align with the line on mobile */
        width: 60px;
        height: 60px;
        top: 0;
        transform: none;

        .emoji {
            font-size: 1.5rem;
        }
    }

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;

        .emoji {
            font-size: 1.3rem;
        }
    }

    @media (max-width: 480px) {
        left: -5px;
    }
`;

// Internal icons for left section
const AdditionalPointIcon = styled(motion.div)`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #990147, #cc3366);
    box-shadow: 0 10px 25px rgba(153, 1, 71, 0.3);

    .emoji {
        font-size: 1.8rem;
        filter: brightness(0) invert(1);
    }

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
        margin-bottom: 1rem;

        .emoji {
            font-size: 1.5rem;
        }
    }
`;

const CardTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    line-height: 1.3;

    @media (max-width: 768px) {
        font-size: 1.3rem;
        margin-bottom: 0.75rem;
    }
`;

const CardDescription = styled.p`
    color: #4a5568;
    line-height: 1.6;
    font-size: 1rem;
    font-weight: 400;

    @media (max-width: 768px) {
        font-size: 0.95rem;
        line-height: 1.5;
    }
`;

// Image Section - Can extend outside container
const ImageSection = styled(motion.div)`
    margin-top: 4rem;

    border-radius: 25px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset;
    position: relative;
    padding-right: -2rem; /* Extend outside container on the right */

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg,
        rgba(153, 1, 71, 0.1) 0%,
        rgba(204, 51, 102, 0.05) 100%);
        z-index: 1;
        pointer-events: none;
    }

    @media (max-width: 968px) {
        margin-top: 2rem;
        margin-right: 0;
    }

    @media (max-width: 768px) {
        margin-top: 2rem;
        border-radius: 20px;
    }
`;

const SectionImage = styled.img`
    width: 100%;
    height: 400px;
    object-fit: cover;
    display: block;
    transition: transform 0.6s ease;

    ${ImageSection}:hover & {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        height: 300px;
    }

    @media (max-width: 480px) {
        height: 250px;
    }
`;

const FloatingOrbs = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const Orb = styled(motion.div)`
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.1), rgba(204, 51, 102, 0.1));
    filter: blur(20px);

    @media (max-width: 768px) {
        display: none; /* Disable orbs on mobile for performance */
    }
`;

const CTAButton = styled(motion.button)`
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 2rem 0 3rem 0;
    box-shadow: 0 10px 30px rgba(153, 1, 71, 0.3);
    display: block;
    margin-left: auto;
    margin-right: auto;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(153, 1, 71, 0.4);
    }

    @media (max-width: 768px) {
        padding: 0.8rem 1.5rem;
        font-size: 0.95rem;
        margin: 1.5rem 0 2rem 0;
        border-radius: 10px;
    }
`;

const PhilosophyApproach: React.FC = () => {
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);

    const timelineData = [
        {
            emoji: "🎯",
            title: "Odbornosť a profesionalita",
            description: "Vysoký štandard lekárskej starostlivosti založený na aktuálnych vedeckých poznatkoch",
            number: "01"
        },
        {
            emoji: "💬",
            title: "Ľudská a etická komunikácia",
            description: "Citlivý a rešpektujúci prístup k každému pacientovi",
            number: "02"
        },
        {
            emoji: "⭐",
            title: "Individualizovaný prístup",
            description: "Liečba šitá na mieru konkrétnym potrebám a situácii pacienta",
            number: "03"
        },
        {
            emoji: "⏰",
            title: "Časová flexibilita",
            description: "Prispôsobenie sa časovým možnostiam pacienta",
            number: "04"
        }
    ];

    const additionalPointsData = [
        {
            emoji: "🔒",
            title: "Maximálna diskrétnosť",
            description: "Dôvernosť a ochrana súkromia ako absolútnu prioritu",
            number: "05"
        },
        {
            emoji: "✨",
            title: "Nadštandardné služby",
            description: "Doplnkové služby pre komplexnú starostlivosť",
            number: "06"
        }
    ];

    return (
        <PhilosophySection id="philosophy">
            <BackgroundPattern />

            <FloatingOrbs>
                {[...Array(6)].map((_, i) => (
                    <Orb
                        key={i}
                        animate={{
                            y: [0, -30, 0],
                            x: [0, Math.random() * 40 - 20, 0],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 5 + Math.random() * 3,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                        }}
                        style={{
                            width: Math.random() * 120 + 60,
                            height: Math.random() * 120 + 60,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </FloatingOrbs>

            <Container>
                <ContentGrid>
                    {/* Top Left: Text Content */}
                    <TextContent>
                        <SectionHeader>
                            <SectionTitle
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                viewport={{ once: true, margin: "-100px" }}
                            >
                                Naša filozofia starostlivosti
                            </SectionTitle>

                            <TitleUnderline
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                viewport={{ once: true }}
                            />

                            <SectionSubtitle
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                Tam, kde štandardná zdravotná starostlivosť nestačí,
                                ponúkame cestu k skutočnej pomoci
                            </SectionSubtitle>
                        </SectionHeader>

                        <MainDescription>
                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                Telesné a duševné zdravie sú dve spojené nádoby. Hoci sa o dôležitosti
                                duševného zdravia veľa píše a hovorí, mnohí ľudia stále trpia bez pomoci,
                                potichu, nezriedka osamote, zbierajú v sebe odhodlanie navštíviť odborníka.
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                Predsudky a bohužiaľ bežná prax deklarovanej štandardnej zdravotnej
                                starostlivosti často vzďaľujú skutočnú pomoc od tých, ktorí ju potrebujú.
                            </motion.p>

                            <HighlightBox
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <p>
                                    "V súčasnosti je pre odborníkov skutočnou výzvou hľadať nové modely
                                    poskytovania plnohodnotnej zdravotnej starostlivosti."
                                </p>
                            </HighlightBox>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                Jednou z možností je vytvorenie nezmluvných ambulancií, ktorých koncept
                                ponúka maximálnu možnú mieru toho, čo pacient potrebuje.
                            </motion.p>

                        </MainDescription>
                    </TextContent>

                    {/* Top Right: Timeline (Points 1-4) */}
                    <TimelineContainer>
                        <Timeline>
                            {timelineData.map((item, index) => (
                                <TimelineItem
                                    key={index}
                                    initial={{ opacity: 0, x: 60 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{
                                        duration: 0.8,
                                        delay: index * 0.15,
                                        ease: "easeOut"
                                    }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    onHoverStart={() => setHoveredCard(index)}
                                    onHoverEnd={() => setHoveredCard(null)}
                                >
                                    <TimelineCard
                                        whileHover={{
                                            scale: 1.03,
                                            transition: { duration: 0.3 }
                                        }}
                                    >
                                        <TimelineIcon
                                            initial={{ scale: 0, rotate: -180 }}
                                            whileInView={{ scale: 1, rotate: 0 }}
                                            transition={{
                                                duration: 0.6,
                                                delay: index * 0.15 + 0.2,
                                                type: "spring"
                                            }}
                                            viewport={{ once: true }}
                                            whileHover={{
                                                scale: 1.1,
                                                rotate: 5,
                                                transition: { duration: 0.3 }
                                            }}
                                        >
                                            <span className="emoji">{item.emoji}</span>
                                        </TimelineIcon>

                                        <CardTitle>{item.number}. {item.title}</CardTitle>
                                        <CardDescription>{item.description}</CardDescription>
                                    </TimelineCard>
                                </TimelineItem>
                            ))}
                        </Timeline>
                    </TimelineContainer>

                    {/* Bottom Left: Additional Points (5-6) */}
                    <AdditionalContent>
                        <AdditionalPointsGrid>
                            {additionalPointsData.map((item, index) => (
                                <AdditionalPointCard
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.8,
                                        delay: 0.5 + index * 0.1,
                                        ease: "easeOut"
                                    }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    whileHover={{
                                        scale: 1.03,
                                        transition: { duration: 0.3 }
                                    }}
                                >
                                    <AdditionalPointIcon
                                        initial={{ scale: 0, rotate: -180 }}
                                        whileInView={{ scale: 1, rotate: 0 }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.6 + index * 0.1,
                                            type: "spring"
                                        }}
                                        viewport={{ once: true }}
                                        whileHover={{
                                            scale: 1.1,
                                            rotate: 5,
                                            transition: { duration: 0.3 }
                                        }}
                                    >
                                        <span className="emoji">{item.emoji}</span>
                                    </AdditionalPointIcon>

                                    <CardTitle>{item.number}. {item.title}</CardTitle>
                                    <CardDescription>{item.description}</CardDescription>
                                </AdditionalPointCard>
                            ))}
                        </AdditionalPointsGrid>
                    </AdditionalContent>

                    {/* Bottom Right: Image */}
                    <ImageSection
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        viewport={{ once: true }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <SectionImage
                            src="/img_1.png"
                            alt="Psychiatrická ambulancia - profesionálna starostlivosť"
                        />
                    </ImageSection>
                </ContentGrid>
            </Container>
        </PhilosophySection>
    );
};

export default PhilosophyApproach;